import React from 'react'
import { clientConfig } from '@/config'
import { marketingContent } from '@/config/marketing'
import { usePricingPlans } from '@/hooks/usePricingPlans'

interface SEOProps {
  title?: string
  description?: string
  url?: string
  image?: string
  noIndex?: boolean
}

const DEFAULT_SITE_URL = 'https://ricald.ai'
const DEFAULT_DESCRIPTION =
  'Ricald connects CRM, email, calendar, and relationship context to coordinate commercial work, act within your rules, and surface decisions for review.'

const asAbsoluteUrl = (value: string, baseUrl: string): string => {
  try {
    return new URL(value, `${baseUrl.replace(/\/$/, '')}/`).toString()
  } catch {
    return value
  }
}

const currentCanonicalUrl = (siteUrl: string): string => {
  if (typeof window === 'undefined') return siteUrl

  const pathname = window.location.pathname === '/' ? '/' : window.location.pathname.replace(/\/$/, '')
  return asAbsoluteUrl(pathname, siteUrl)
}

const setMeta = (
  attribute: 'name' | 'property',
  key: string,
  content: string,
): void => {
  const selector = `meta[${attribute}="${key}"]`
  const matches = Array.from(document.head.querySelectorAll<HTMLMetaElement>(selector))
  const element = matches.shift() || document.createElement('meta')

  element.setAttribute(attribute, key)
  element.content = content
  element.dataset.ricaldSeo = 'true'
  if (!element.parentNode) document.head.appendChild(element)
  matches.forEach((duplicate) => duplicate.remove())
}

const removeMeta = (attribute: 'name' | 'property', key: string): void => {
  document.head
    .querySelectorAll(`meta[${attribute}="${key}"]`)
    .forEach((element) => element.remove())
}

const setCanonical = (url: string): void => {
  const matches = Array.from(
    document.head.querySelectorAll<HTMLLinkElement>('link[rel="canonical"]'),
  )
  const element = matches.shift() || document.createElement('link')

  element.rel = 'canonical'
  element.href = url
  element.dataset.ricaldSeo = 'true'
  if (!element.parentNode) document.head.appendChild(element)
  matches.forEach((duplicate) => duplicate.remove())
}

export const SEO: React.FC<SEOProps> = ({
  title = `${clientConfig.branding.productName} — ${clientConfig.branding.tagline}`,
  description,
  url,
  image,
  noIndex = false,
}) => {
  const { marketingCopy } = usePricingPlans()
  const siteUrl = (
    marketingCopy.landing_v3_meta_url ||
    marketingCopy.meta_url ||
    marketingContent.metadata.url ||
    DEFAULT_SITE_URL
  ).replace(/\/$/, '')
  // Page-specific props must win. Otherwise a legacy server value can replace
  // the current page narrative after the pricing request resolves.
  const resolvedDescription =
    description?.trim() ||
    marketingCopy.landing_v3_seo_description ||
    marketingCopy.seo_description ||
    DEFAULT_DESCRIPTION
  const productDescription =
    marketingCopy.landing_v3_seo_description || DEFAULT_DESCRIPTION
  const resolvedUrl = url ? asAbsoluteUrl(url, siteUrl) : currentCanonicalUrl(siteUrl)
  const imageValue =
    image ||
    marketingCopy.landing_v3_meta_og_image ||
    marketingCopy.meta_og_image ||
    marketingContent.metadata.ogImage
  const resolvedImage = asAbsoluteUrl(imageValue, siteUrl)
  const resolvedTwitterHandle =
    marketingCopy.meta_twitter_handle || marketingContent.metadata.twitterHandle || ''
  const resolvedSubcategory =
    marketingCopy.landing_v3_seo_subcategory ||
    marketingCopy.seo_application_subcategory ||
    'Commercial Operations Software'

  const jsonLd = React.useMemo(() => ({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${siteUrl}/#organization`,
        name: clientConfig.branding.companyName,
        url: siteUrl,
      },
      {
        '@type': 'WebSite',
        '@id': `${siteUrl}/#website`,
        name: clientConfig.branding.productName,
        url: siteUrl,
        publisher: { '@id': `${siteUrl}/#organization` },
      },
      {
        '@type': 'SoftwareApplication',
        '@id': `${siteUrl}/#software`,
        name: clientConfig.branding.productName,
        url: siteUrl,
        applicationCategory: 'BusinessApplication',
        applicationSubCategory: resolvedSubcategory,
        operatingSystem: 'Web',
        description: productDescription,
        featureList: [
          'Persistent commercial objectives',
          'Connected context across CRM, email, calendar, and relationship data',
          'Coordinated research, qualification, and outreach',
          'Explicit action authority and approval boundaries',
          'Explainable recommendations with supporting evidence',
          'Auditable activity history',
          'Shared commercial memory informed by outcomes',
        ],
        audience: {
          '@type': 'BusinessAudience',
          audienceType: 'B2B commercial teams building AI-native operating models',
        },
        publisher: { '@id': `${siteUrl}/#organization` },
        softwareHelp: {
          '@type': 'CreativeWork',
          url: `${siteUrl}/contact`,
        },
      },
    ],
  }), [productDescription, resolvedSubcategory, siteUrl])

  React.useLayoutEffect(() => {
    document.title = title
    setMeta('name', 'description', resolvedDescription)
    setMeta('name', 'robots', noIndex ? 'noindex, nofollow' : 'index, follow')
    setCanonical(resolvedUrl)
    setMeta('property', 'og:site_name', clientConfig.branding.productName)
    setMeta('property', 'og:title', title)
    setMeta('property', 'og:description', resolvedDescription)
    setMeta('property', 'og:type', 'website')
    setMeta('property', 'og:url', resolvedUrl)
    setMeta('property', 'og:image', resolvedImage)
    setMeta('property', 'og:image:width', '1200')
    setMeta('property', 'og:image:height', '630')
    setMeta('property', 'og:image:alt', 'Ricald commercial operation overview')
    setMeta('name', 'twitter:card', 'summary_large_image')
    setMeta('name', 'twitter:title', title)
    setMeta('name', 'twitter:description', resolvedDescription)
    setMeta('name', 'twitter:image', resolvedImage)

    if (resolvedTwitterHandle) {
      setMeta('name', 'twitter:site', resolvedTwitterHandle)
    } else {
      removeMeta('name', 'twitter:site')
    }

    const existingJsonLd = document.head.querySelector<HTMLScriptElement>(
      'script[data-ricald-seo-jsonld="true"]',
    )
    if (noIndex) {
      existingJsonLd?.remove()
    } else {
      const script = existingJsonLd || document.createElement('script')
      script.type = 'application/ld+json'
      script.dataset.ricaldSeoJsonld = 'true'
      script.textContent = JSON.stringify(jsonLd)
      if (!script.parentNode) document.head.appendChild(script)
    }
  }, [
    jsonLd,
    noIndex,
    resolvedDescription,
    resolvedImage,
    resolvedTwitterHandle,
    resolvedUrl,
    title,
  ])

  return null
}

export default SEO
